/** White-label branding editor — Phase 3-pp-brand.
 *
 * Lets the org admin override logo, accent colors, product name, and
 * support contact info. Empty fields = use the platform defaults.
 * The bootstrap path (`/v1/branding/by-host`, Phase 3-pp-domain) reads
 * this same row when a custom-domain Host header matches.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import type { Branding, BrandingInput } from "@/lib/types";

const EMPTY: Branding = {
  logo_url: "",
  wordmark_url: "",
  favicon_url: "",
  primary_color: "",
  accent_color: "",
  product_name_override: "",
  support_email: "",
  support_url: "",
  email_from_name: "",
  pdf_footer_text: "",
  updated_at: "",
};

export function BrandingConfigCard() {
  const qc = useQueryClient();
  const { t } = useTranslation();
  const [form, setForm] = useState<Branding>(EMPTY);
  const [seeded, setSeeded] = useState(false);

  const branding = useQuery({
    queryKey: ["org-branding"],
    queryFn: async () => {
      const res = await api.get<Branding>(ApiPaths.orgBranding);
      return res.data;
    },
  });

  useEffect(() => {
    if (seeded || !branding.data) return;
    setForm(branding.data);
    setSeeded(true);
  }, [branding.data, seeded]);

  const save = useMutation({
    mutationFn: async (input: BrandingInput) => {
      const res = await api.patch<Branding>(ApiPaths.orgBranding, input);
      return res.data;
    },
    onSuccess: (next) => {
      qc.setQueryData(["org-branding"], next);
    },
  });

  // Campaigns/Sessions v2 — upload the logo image (stored in S3, logo_url
  // set to a stable serve URL). Refreshes the form + PDF/dropdown source.
  const uploadLogo = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("logo", file);
      const res = await api.post<Branding>(ApiPaths.orgBrandingLogo, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return res.data;
    },
    onSuccess: (next) => {
      qc.setQueryData(["org-branding"], next);
      setForm((f) => ({ ...f, logo_url: next.logo_url }));
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const { updated_at: _ignored, ...rest } = form;
    save.mutate(rest);
  };

  if (branding.isLoading && !branding.isFetched) {
    return <section className="card text-sm text-nb-text-2">Loading…</section>;
  }
  if (!branding.data) return null;

  return (
    <section className="card space-y-4" data-testid="branding-card">
      <header>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {t("admin.branding.cardTitle")}
        </h2>
        <p className="text-xs text-nb-text-3">{t("admin.branding.cardHint")}</p>
      </header>

      <form className="space-y-3" onSubmit={onSubmit}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="logo_url">{t("admin.branding.logoUrl")}</label>
            <div className="mb-2 flex items-center gap-3">
              {form.logo_url && (
                <img
                  src={form.logo_url}
                  alt=""
                  className="h-10 w-10 rounded border border-nb-border object-contain"
                />
              )}
              <label className="btn-ghost cursor-pointer text-xs">
                {uploadLogo.isPending
                  ? t("common.loading")
                  : t("admin.branding.uploadLogo")}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadLogo.mutate(f);
                    e.target.value = "";
                  }}
                />
              </label>
            </div>
            <input
              id="logo_url"
              className="input"
              value={form.logo_url}
              placeholder="https://cdn.acme.com/logo.png"
              onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
            />
          </div>
          <div>
            <label className="label" htmlFor="wordmark_url">{t("admin.branding.wordmarkUrl")}</label>
            <input
              id="wordmark_url"
              className="input"
              value={form.wordmark_url}
              placeholder={t("admin.branding.wordmarkHint")}
              onChange={(e) => setForm({ ...form, wordmark_url: e.target.value })}
            />
          </div>
          <div>
            <label className="label" htmlFor="favicon_url">{t("admin.branding.faviconUrl")}</label>
            <input
              id="favicon_url"
              className="input"
              value={form.favicon_url}
              placeholder={t("admin.branding.faviconHint")}
              onChange={(e) => setForm({ ...form, favicon_url: e.target.value })}
            />
          </div>
          <div>
            <label className="label" htmlFor="product_name_override">
              {t("admin.branding.productNameOverride")}
            </label>
            <input
              id="product_name_override"
              className="input"
              value={form.product_name_override}
              placeholder="Acme Inspect"
              onChange={(e) =>
                setForm({ ...form, product_name_override: e.target.value })
              }
            />
          </div>
          <div>
            <label className="label" htmlFor="primary_color">{t("admin.branding.primaryColor")}</label>
            <input
              id="primary_color"
              className="input"
              value={form.primary_color}
              placeholder="#0F172A"
              maxLength={7}
              onChange={(e) =>
                setForm({ ...form, primary_color: e.target.value })
              }
            />
            <p className="mt-1 text-xs text-nb-text-3">{t("admin.branding.primaryColorHint")}</p>
          </div>
          <div>
            <label className="label" htmlFor="accent_color">{t("admin.branding.accentColor")}</label>
            <input
              id="accent_color"
              className="input"
              value={form.accent_color}
              placeholder="#22D3EE"
              maxLength={7}
              onChange={(e) =>
                setForm({ ...form, accent_color: e.target.value })
              }
            />
          </div>
          <div>
            <label className="label" htmlFor="support_email">{t("admin.branding.supportEmail")}</label>
            <input
              id="support_email"
              type="email"
              className="input"
              value={form.support_email}
              placeholder="help@acme.com"
              onChange={(e) =>
                setForm({ ...form, support_email: e.target.value })
              }
            />
          </div>
          <div>
            <label className="label" htmlFor="support_url">{t("admin.branding.supportUrl")}</label>
            <input
              id="support_url"
              className="input"
              value={form.support_url}
              placeholder="https://help.acme.com"
              onChange={(e) => setForm({ ...form, support_url: e.target.value })}
            />
          </div>
          <div>
            <label className="label" htmlFor="email_from_name">
              {t("admin.branding.emailFromName")}
            </label>
            <input
              id="email_from_name"
              className="input"
              value={form.email_from_name}
              placeholder="Acme Inspections"
              onChange={(e) =>
                setForm({ ...form, email_from_name: e.target.value })
              }
            />
          </div>
          <div>
            <label className="label" htmlFor="pdf_footer_text">{t("admin.branding.pdfFooterText")}</label>
            <input
              id="pdf_footer_text"
              className="input"
              value={form.pdf_footer_text}
              placeholder="© 2026 Acme Inspections"
              onChange={(e) =>
                setForm({ ...form, pdf_footer_text: e.target.value })
              }
            />
          </div>
        </div>

        {save.isError && (
          <p className="text-xs text-cvp-danger" role="alert">
            {t("admin.branding.saveError")}
          </p>
        )}
        {save.isSuccess && (
          <p className="text-xs text-emerald-400" role="status">
            {t("common.saved")}
          </p>
        )}

        <button
          type="submit"
          className="btn-primary text-sm"
          disabled={save.isPending}
        >
          {save.isPending ? t("common.saving") : t("admin.branding.saveButton")}
        </button>
      </form>
    </section>
  );
}
